/* eslint-disable camelcase */
import { useState } from 'react';
import AccountsSDK from '@livechat/accounts-sdk';
import { ApolloError, gql, useMutation } from '@apollo/client';
import Cookies from 'js-cookie';

import Config from 'Config';
import { Cookie } from 'constants/cookies';
import { LocalStorageKey } from 'constants/local-storage';
import { useNotifications } from 'contexts/notifications/use-notifications';
import Location from 'services/Location';
import { detectProduct, getProductName } from 'helpers/product';
import { getAccessToken } from 'helpers/account';
import { navigateToSelectedProduct, navigateToOriginalUrl } from 'helpers/navigation';
import Amplitude from 'services/Amplitude';
import LocalStorage from 'services/LocalStorage';

export const VERIFY_ACCOUNT_MUTATION = gql`
  mutation VerifyAccount($code: String!) {
    VerifyAccount(code: $code)
  }
`;

type UseSendVerificationCode = {
  handleVerify(code: string): void;
  clearError(): void;
  isProcessing: boolean;
  error: string | null;
  data: boolean;
};

export const useVerifyAccount = (
  isAccountWhitelisted: boolean,
  email: string,
  onVerificationWithoutProductCompleted: () => void
): UseSendVerificationCode => {
  const [error, setError] = useState(null);
  const { showToast } = useNotifications();
  const accessToken = getAccessToken();

  const handleError = (err: ApolloError): void => {
    if (err.message === 'resource_not_found' || err.message === 'invalid_request') {
      setError('Incorrect or expired code. Try again or get a new code.');
      return;
    }

    setError(null);

    if (err.message === 'unauthorized') {
      Cookies.remove(Cookie.VerificationCodeSend);
      navigateToOriginalUrl();
      return;
    }

    showToast({
      content: "We couldn't verify your account. Please contact support",
      variant: 'error',
    });
  };

  const handleSuccess = async (): Promise<void> => {
    const currentProductCookie = detectProduct();
    const clientID = Location.getUrlParam('client_id');

    showToast({
      content: 'Your email is confirmed',
      variant: 'info',
    });

    Amplitude.logEvent(
      email,
      'EMAIL_VERIFIED',
      {
        product: getProductName(currentProductCookie),
      },
      true
    );

    Cookies.remove(Cookie.VerificationCodeSend);

    if (LocalStorage.get(LocalStorageKey.AccountsVerificationCodeReminder)) {
      Amplitude.logEvent(email, 'ACCOUNT_CONFIRMATION_AFTER_REMINDER');
      LocalStorage.remove(LocalStorageKey.AccountsVerificationCodeReminder);
    }

    if (
      ((!clientID || clientID === Config.default_client_id) && currentProductCookie === 0) ||
      clientID === import.meta.env.VITE_APP_CLIENT_ID ||
      isAccountWhitelisted
    ) {
      try {
        const sdk = new AccountsSDK({
          client_id: Config.organization_app_client_id,
          server_url: Config.accounts_url,
        });
        const response = await sdk.iframe().authorize();
        Cookies.set('access_token', response.access_token, { secure: true, sameSite: 'none' });

        onVerificationWithoutProductCompleted();
      } catch (err) {
        window.location.reload();
      }
      return;
    }

    navigateToSelectedProduct(email);
  };

  const [verify, { data, loading }] = useMutation<boolean>(VERIFY_ACCOUNT_MUTATION, {
    context: {
      headers: {
        Authorization: `Bearer ${accessToken}`,
      },
    },
    onError: handleError,
    onCompleted: handleSuccess,
  });

  const handleVerify = async (code: string): Promise<void> => {
    verify({ variables: { code } });
  };

  return {
    handleVerify,
    clearError: () => setError(null),
    isProcessing: loading,
    data,
    error,
  };
};
